import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { TopicTreeNode } from '../../../../types/TopicCustomization';
import { updateTopicTree } from '../../../../services/topicCustomization';
import RegenerateModal from '../../RegenerateModal';
import { useEffect, useRef, useState } from 'react';
import { hapticsImpactLight } from '../../../../utils/haptics';
import { cloneDeep } from 'lodash';
import Tooltip from '@mui/material/Tooltip';
import { setActiveFilters } from '../../../../slices/activeTopics/activeTopicsSlice';
import CustomModal from '../../CustomModal';
import { flattenTopicTree } from '../../../../utils/flattenTopicTree';
import TopicTree from '../../TopicTree';
import { examsSelectors } from '../../../../slices/exams/examsSlice';

interface TopicSelectProps {
  setIsLoadingStudySessionAndContent: (isLoading: boolean) => void;
  setIsLoadedStudySessionAndContent: (isLoaded: boolean) => void;
  refreshStudySessionAndContent: () => void;
}

const TopicSelect: React.FC<TopicSelectProps> = ({
  setIsLoadingStudySessionAndContent,
  setIsLoadedStudySessionAndContent,
  refreshStudySessionAndContent,
}) => {
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);

  const todaySession = useSelector((state: RootState) => state.todaySession);

  const topicTreeFromRedux =
    useSelector((state: RootState) => state.activeTopics.topicTree) || [];

  const allTopicsActiveFromRedux = useSelector(
    (state: RootState) => state.activeTopics.allTopicsActive
  );
  const allTopicsInactiveFromRedux = useSelector(
    (state: RootState) => state.activeTopics.allTopicsInactive
  );

  const [tempTopicFilters, setTempTopicFilters] = useState<TopicTreeNode[]>([]);
  const [allTopicsActiveLocal, setAllTopicsActiveLocal] = useState(false);
  const [allTopicsInactiveLocal, setAllTopicsInactiveLocal] = useState(false);

  const [showTopicFilters, setShowTopicFilters] = useState(false);
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const topicMenuButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (showTopicFilters) {
      const localCopy = cloneDeep(topicTreeFromRedux);
      setTempTopicFilters(localCopy);
      setAllTopicsActiveLocal(allTopicsActiveFromRedux);
      setAllTopicsInactiveLocal(allTopicsInactiveFromRedux);
      setUnsavedChanges(false);
    }
  }, [
    showTopicFilters,
    topicTreeFromRedux,
    allTopicsActiveFromRedux,
    allTopicsInactiveFromRedux,
  ]);

  const renderTopicFilterSelections = () => {
    if (allTopicsActiveFromRedux) {
      return 'All Topics';
    } else {
      const activeTopics = topicTreeFromRedux.filter(
        (topic: TopicTreeNode) => topic.active
      );
      return activeTopics[0]?.name || 'No topics selected';
    }
  };

  const saveChangesOrShowConfirmation = async () => {
    setShowTopicFilters(false);
    if (todaySession.isStarted === false) {
      submitChanges(true);
    } else if (todaySession.isComplete) {
      submitChanges(false);
    } else {
      setShowRegenModal(true);
    }
  };

  const submitChanges = async (regenerate: boolean) => {
    if (unsavedChanges) {
      setIsLoadingStudySessionAndContent(true);
      setIsLoadedStudySessionAndContent(false);

      const flatTopics = flattenTopicTree(tempTopicFilters);
      await updateTopicTree(flatTopics, regenerate);
      await refreshStudySessionAndContent();

      dispatch(
        setActiveFilters({
          allTopicsActive: allTopicsActiveLocal,
          allTopicsInactive: allTopicsInactiveLocal,
          topicTree: tempTopicFilters,
        })
      );

      setIsLoadingStudySessionAndContent(false);
      setIsLoadedStudySessionAndContent(true);
      setUnsavedChanges(false);
    }
  };

  const handleUnsavedChanges = () => {
    if (cancelButtonRef.current) {
      cancelButtonRef.current.classList.add('shake');
    }
    if (submitButtonRef.current) {
      submitButtonRef.current.classList.add('shake');
    }
    setTimeout(() => {
      cancelButtonRef.current?.classList.remove('shake');
      submitButtonRef.current?.classList.remove('shake');
    }, 500);
  };

  const handleTopicChange = (
    topics: TopicTreeNode[],
    hasChanges: boolean,
    allActive: boolean,
    allInactive: boolean
  ) => {
    setTempTopicFilters(topics);
    setUnsavedChanges(hasChanges);
    setAllTopicsActiveLocal(allActive);
    setAllTopicsInactiveLocal(allInactive);
  };

  return (
    <>
      <button
        id="topic-filters-button"
        className={`button button--glass button--topic-filters ${
          !allTopicsActiveFromRedux ? 'fill' : ''
        }`}
        onClick={() => {
          setShowTopicFilters(true);
          hapticsImpactLight();
        }}
        tabIndex={0}
        ref={topicMenuButtonRef}
      >
        <div className="button--topic-filters_label">Topic Filters</div>
        <div className="button--topic-filters_value">
          <span className="value">{renderTopicFilterSelections()}</span>
          <span className="spacer">X</span>
        </div>
      </button>
      <CustomModal
        open={showTopicFilters}
        onClose={() => setShowTopicFilters(false)}
        name={'topic-select'}
        glass={true}
        unsavedChanges={unsavedChanges}
        handleUnsavedChanges={handleUnsavedChanges}
      >
        <div className="modal_header">
          <div className="exam-title">
            <span>{primaryExam?.name}</span>
            <h2>Topic Filters</h2>
          </div>
        </div>
        <div className="modal_content">
          <TopicTree onTopicChange={handleTopicChange} useSwitches={true} />
        </div>
        <div className="modal_actions">
          <button
            className="button button--glass"
            onClick={() => setShowTopicFilters(false)}
            ref={cancelButtonRef}
          >
            Cancel
          </button>
          <Tooltip
            title={
              allTopicsInactiveLocal
                ? 'You must have at least one active topic.'
                : ''
            }
            enterTouchDelay={0}
          >
            <span>
              <button
                ref={submitButtonRef}
                className="button button--glass"
                onClick={saveChangesOrShowConfirmation}
                disabled={!unsavedChanges || allTopicsInactiveLocal}
              >
                Confirm
              </button>
            </span>
          </Tooltip>
        </div>
      </CustomModal>
      <RegenerateModal
        showRegenModal={showRegenModal}
        setShowRegenModal={setShowRegenModal}
        submitChanges={submitChanges}
        type={'exam-settings'}
      />
    </>
  );
};

export default TopicSelect;
